// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@bg/App/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { InvoicesDataViewContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/InvoiceHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { DatePickerState } from "@insite/mobius/DatePicker";
import Tag, { horizontalStyles, TagPresentationProps } from "@insite/mobius/Tag";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    filtersOpen: state.pages.invoiceHistory.filtersOpen,
    getInvoicesParameter: state.pages.invoiceHistory.getInvoicesParameter,
    ProductErpNumber: state.commonActions["invoiceHistoryProductErpNumber"],
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceHistoryTagsStyles {
    appliedFiltersContainer?: InjectableCss;
}

export const invoiceHistoryFilterStyles: InvoiceHistoryTagsStyles = {
    appliedFiltersContainer: {
        css: css`
            ${horizontalStyles}
            && div[class*=""TagStyle-sc] {
                width: auto;
                height: auto !important;
            }
        `,
    },
};

let updateTimeoutId: number | undefined;
const styles = invoiceHistoryFilterStyles;

const invoiceHistoryFilterTags = ({
    filtersOpen,
    getInvoicesParameter,
    updateSearchFields,
    ProductErpNumber,
}: Props) => {
    if (filtersOpen) {
        return null;
    }

    const dispatch = useDispatch();
    const invoicesDataView = useContext(InvoicesDataViewContext);
    if (invoicesDataView.isLoading) {
        return null;
    }
    const showOpenOnlyChangeHandler = (showOpenOnly: boolean) => {
        updateSearchFields({ showOpenOnly: showOpenOnly || false });
    };

    const shipToChangeHandler = (customerSequence: string) => {
        updateSearchFields({ customerSequence: customerSequence || "" });
    };

    const invoiceNumberChangeHandler = () => {
        if (typeof updateTimeoutId === "number") {
            clearTimeout(updateTimeoutId);
        }

        updateTimeoutId = setTimeout(() => {
            updateSearchFields({
                invoiceNumber: "",
            });
        }, 250);
    };

    const orderNumberChangeHandler = () => {
        if (typeof updateTimeoutId === "number") {
            clearTimeout(updateTimeoutId);
        }

        updateTimeoutId = setTimeout(() => {
            updateSearchFields({
                orderNumber: "",
            });
        }, 250);
    };
    const poNumberChangeHandler = () => {
        if (typeof updateTimeoutId === "number") {
            clearTimeout(updateTimeoutId);
        }

        updateTimeoutId = setTimeout(() => {
            updateSearchFields({
                poNumber: "",
            });
        }, 250);
    };

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            fromDate: "",
        });
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            toDate: "",
        });
    };

    const erpChangeHandler = () => {
        if (typeof updateTimeoutId === "number") {
            clearTimeout(updateTimeoutId);
        }

        updateTimeoutId = setTimeout(() => {
            updateSearchFields({
                productErpNumber: "",
            } as any);
            dispatch({
                type: "commonActionSetter/SetAction",
                payload: {
                    name: "invoiceHistoryProductErpNumber",
                    value: "",
                },
            });
        }, 250);
    };

    return (
        <StyledWrapper {...styles.appliedFiltersContainer}>
            {getInvoicesParameter.customerSequence && getInvoicesParameter.customerSequence !== "-1" && (
                <Tag
                    onDelete={() => {
                        shipToChangeHandler("-1");
                    }}
                >
                    {`${translate("Ship To")}: ${getInvoicesParameter.customerSequence}`}
                </Tag>
            )}
            {getInvoicesParameter.invoiceNumber && (
                <Tag
                    onDelete={() => {
                        invoiceNumberChangeHandler();
                    }}
                >
                    {`${translate("Invoice #")}: ${getInvoicesParameter.invoiceNumber}`}
                </Tag>
            )}
            {getInvoicesParameter.orderNumber && (
                <Tag
                    onDelete={() => {
                        orderNumberChangeHandler();
                    }}
                >
                    {`${translate("Order #")}: ${getInvoicesParameter.orderNumber}`}
                </Tag>
            )}
            {getInvoicesParameter.poNumber && (
                <Tag
                    onDelete={() => {
                        poNumberChangeHandler();
                    }}
                >
                    {`${translate("PO #")}: ${getInvoicesParameter.poNumber}`}
                </Tag>
            )}
            {getInvoicesParameter.fromDate && (
                <Tag
                    onDelete={() => {
                        fromDateChangeHandler({} as any);
                    }}
                    data-test-selector="invoiceHistory_fromDateButton"
                >
                    {`${translate("From")}: ${getInvoicesParameter.fromDate}`}
                </Tag>
            )}
            {getInvoicesParameter.toDate && (
                <Tag
                    onDelete={() => {
                        toDateChangeHandler({} as any);
                    }}
                >
                    {`${translate("To")}: ${getInvoicesParameter.toDate}`}
                </Tag>
            )}
            {ProductErpNumber && (
                <Tag
                    onDelete={() => {
                        erpChangeHandler();
                    }}
                >
                    {`${translate("Product Erp #")}: ${ProductErpNumber}`}
                </Tag>
            )}
            {getInvoicesParameter.showOpenOnly && (
                <Tag
                    onDelete={() => {
                        showOpenOnlyChangeHandler(false);
                    }}
                >
                    {translate("Only open")}
                </Tag>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(invoiceHistoryFilterTags),
    definition: {
        group: "XNGAGE" as any,
        displayName: "Tags",
        allowedContexts: ["InvoiceHistoryPage"],
    },
};

export default widgetModule;
